import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchNetworks, fetchNetworkById, createNetwork, updateNetwork, deleteNetwork } from '../api';

/**
 * Hook for fetching all networks
 * @returns Query result with networks data
 */
export const useNetworks = () => {
  return useQuery({
    queryKey: ['networks'],
    queryFn: fetchNetworks
  });
};

/**
 * Hook for fetching a network by ID
 * @param id Network ID
 * @returns Query result with network data
 */
export const useNetwork = (id: string) => {
  return useQuery({
    queryKey: ['network', id],
    queryFn: () => fetchNetworkById(id),
    enabled: !!id // Only run the query if id is provided
  });
};

/**
 * Hook for creating a new network
 * @returns Mutation for creating a network
 */
export const useCreateNetwork = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (networkData: any) => createNetwork(networkData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
};

/**
 * Hook for updating a network
 * @returns Mutation for updating a network
 */
export const useUpdateNetwork = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, networkData }: { id: string, networkData: any }) => updateNetwork(id, networkData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
      queryClient.invalidateQueries({ queryKey: ['network', variables.id] });
    }
  });
};

/**
 * Hook for deleting a network
 * @returns Mutation for deleting a network
 */
export const useDeleteNetwork = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => deleteNetwork(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['networks'] });
    }
  });
};
